import { useEffect, useState } from 'react';
import { getV1AccessMode } from 'src/services/v1AccessMode';
import { V1_ACCESS_MODES, V1_ACCESS_POLL_INTERVAL_MS, V1_BYPASS_PARAM, V1_BYPASS_STORAGE_KEY } from 'src/lib/constants';

const isKnownMode = (value) => Object.prototype.hasOwnProperty.call(V1_ACCESS_MODES, value);

/**
 * A deliberate hop from V2 ("continue to the old platform") carries ?keepV1=1.
 * Remember it for the session so the notice does not bounce them back.
 */
export function readBypass() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(V1_BYPASS_PARAM) === '1') {
      window.sessionStorage.setItem(V1_BYPASS_STORAGE_KEY, '1');
      return true;
    }
    return window.sessionStorage.getItem(V1_BYPASS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Reads the cutover flag the V2 admin controls. Every failure path resolves to
 * `off` — a platform that cannot be reached must never strand people on V1.
 */
export default function useV1AccessMode() {
  const [mode, setMode] = useState(V1_ACCESS_MODES.off);

  useEffect(() => {
    const apiUrl = process.env.GATSBY_V2_API_URL;
    const appUrl = process.env.GATSBY_V2_APP_URL;
    // Without somewhere to send people, a notice is just a dead end.
    if (!apiUrl || !appUrl) {
      console.warn(
        '[v1-migration] GATSBY_V2_API_URL / GATSBY_V2_APP_URL are not set, so the migration notice is disabled. Add them to .env.development (or .env.production) and restart Gatsby.'
      );
      return undefined;
    }
    if (readBypass()) return undefined;

    let cancelled = false;

    const read = async () => {
      try {
        const next = await getV1AccessMode(apiUrl);
        if (!cancelled) setMode(isKnownMode(next) ? next : V1_ACCESS_MODES.off);
      } catch {
        if (!cancelled) setMode(V1_ACCESS_MODES.off);
      }
    };

    read();
    const poll = setInterval(read, V1_ACCESS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, []);

  return mode;
}
